interface PageMetaProps {
  title: string;
  description: string;
  canonicalPath?: string;
  noindex?: boolean;
  ogImage?: string;
  structuredData?: Record<string, unknown>;
}

export default function PageMeta({
  title,
  description,
  canonicalPath,
  noindex,
  ogImage,
  structuredData,
}: PageMetaProps) {
  const canonical = canonicalPath
    ? `https://lelyric.sonbook.net${canonicalPath}`
    : 'https://lelyric.sonbook.net/';

  document.title = title;

  const setMeta = (name: string, content: string, prop?: boolean) => {
    let el: HTMLMetaElement | null;
    if (prop) {
      el = document.querySelector(`meta[property="${name}"]`);
    } else {
      el = document.querySelector(`meta[name="${name}"]`);
    }
    if (!el) {
      el = document.createElement('meta');
      if (prop) {
        el.setAttribute('property', name);
      } else {
        el.setAttribute('name', name);
      }
      document.head.appendChild(el);
    }
    el.setAttribute('content', content);
  };

  const setLink = (rel: string, href: string) => {
    let el = document.querySelector(`link[rel="${rel}"]`);
    if (!el) {
      el = document.createElement('link');
      el.setAttribute('rel', rel);
      document.head.appendChild(el);
    }
    el.setAttribute('href', href);
  };

  // Always reset robots — prevents stale noindex when navigating between pages in SPA
  setMeta('robots', noindex ? 'noindex, follow' : 'index, follow');

  setMeta('description', description);
  setMeta('og:title', title, true);
  setMeta('og:description', description, true);
  setMeta('og:url', canonical, true);
  setMeta('twitter:title', title);
  setMeta('twitter:description', description);
  setLink('canonical', canonical);

  if (ogImage) {
    setMeta('og:image', ogImage, true);
    setMeta('twitter:image', ogImage);
  }

  // Per-page structured data JSON-LD (e.g. BreadcrumbList)
  const schemaId = 'page-ld-json';
  if (structuredData) {
    let script = document.getElementById(schemaId) as HTMLScriptElement | null;
    if (!script) {
      script = document.createElement('script');
      script.type = 'application/ld+json';
      script.id = schemaId;
      document.head.appendChild(script);
    }
    script.textContent = JSON.stringify(structuredData);
  } else {
    const existing = document.getElementById(schemaId);
    if (existing) existing.remove();
  }

  return null;
}